/**
 * Chat Thread Commands
 * 
 * Commands for creating threads and sending messages in SpaceChat.
 */

import { SelfContainedCommand, CommandExecutionContext } from '../../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../../types/standardToolResults';
import { createFileLogger } from '../../../../utils/logger';
import { GROK_MODELS } from '../../../../config/grok';

const log = createFileLogger('CHAT_THREAD_COMMANDS');

/**
 * Create a new chat thread and optionally send an initial message
 */
export const chatCreateThreadCommand: SelfContainedCommand = {
  id: 'chat_createThread',
  description: 'Create a new chat thread and optionally send an initial message',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',
  
  schema: {
    parameters: [
      {
        name: 'message',
        type: 'string',
        description: 'The initial message to send in the new thread',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) return 'Message cannot be empty';
          if (value.length > 50000) return 'Message too long (max 50k chars)';
          return null;
        },
        examples: [
          'Help me write a story about dragons',
          'What are the best practices for React hooks?'
        ]
      },
      {
        name: 'threadTitle',
        type: 'string',
        description: 'Optional title for the thread',
        required: false,
        defaultValue: 'New Chat',
        examples: ['Dragon Story', 'React Hooks Discussion']
      },
      {
        name: 'model',
        type: 'string',
        description: 'Optional model to use for this thread (e.g., GROK_4_PREMIUM for NSFW content)',
        required: false,
        examples: [
          'claude/claude-sonnet-4.5',
          GROK_MODELS.GROK_4_PREMIUM,
          'claude/claude-haiku-4.5'
        ]
      },
      {
        name: 'folderId',
        type: 'string',
        description: 'Optional folder/group ID to place the thread in',
        required: false,
        examples: ['folder-123', 'group-456']
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { 
          message: 'Help me write a fantasy story',
          threadTitle: 'Fantasy Story'
        },
        expectedOutput: createSuccessResult('Thread created successfully', {
          threadId: 'thread-123',
          threadTitle: 'Fantasy Story',
          messageSent: true
        }),
        description: 'Create a new thread with a message and title',
        context: 'When starting a new conversation'
      },
      {
        input: { 
          message: 'Write an adult story',
          threadTitle: 'NSFW Story',
          model: GROK_MODELS.GROK_4_PREMIUM
        },
        expectedOutput: createSuccessResult('Thread created successfully', {
          threadId: 'thread-456',
          threadTitle: 'NSFW Story',
          model: GROK_MODELS.GROK_4_PREMIUM,
          messageSent: true
        }),
        description: 'Create a thread with NSFW content using Grok Premium',
        context: 'When creating uncensored content'
      }
    ],
    bad: [
      {
        input: { message: '' },
        error: 'Message cannot be empty',
        why: 'Empty messages are not allowed',
        fix: 'Provide a non-empty message'
      }
    ]
  },
  
  performance: {
    expectedDuration: 100,
    complexity: 'low'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { message, threadTitle, model, folderId } = params;
    
    try {
      log.log('[chat_createThread] Creating thread and sending message:', {
        messageLength: message?.length,
        threadTitle,
        model,
        folderId,
        hasContext: !!context
      });
      
      // Dispatch custom event to trigger thread creation
      // This integrates with the existing Space.tsx event listener
      window.dispatchEvent(new CustomEvent('spacechat:sendPrompt', {
        detail: {
          prompt: message,
          threadTitle: threadTitle || 'New Chat',
          model: model || undefined,
          folderId: folderId || undefined
        }
      }));
      
      log.log('[chat_createThread] ✅ Event dispatched successfully');
      
      return createSuccessResult(
        `✅ Chat thread created: "${threadTitle || 'New Chat'}"${folderId ? ' in folder' : ''}`,
        {
          threadCreated: true,
          threadTitle: threadTitle || 'New Chat',
          messageSent: true,
          messageLength: message.length,
          model: model || 'default',
          folderId: folderId || null
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'event-dispatch',
            affectedEntities: []
          },
          ui: {
            refreshContext: false,
            resultPreview: 'summary'
          }
        }
      );
      
    } catch (error) {
      log.error('[chat_createThread] Error:', error);
      return createErrorResult(
        'Failed to create chat thread',
        error instanceof Error ? error.message : 'Unknown error',
        'execution',
        { 
          data: { 
            messageLength: message?.length || 0,
            threadTitle,
            model,
            folderId
          },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};

/**
 * Send a message to the current thread
 */
export const chatSendMessageCommand: SelfContainedCommand = {
  id: 'chat_sendMessage',
  description: 'Send a message in the current chat thread',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',
  
  schema: {
    parameters: [
      {
        name: 'message',
        type: 'string',
        description: 'The message to send',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) return 'Message cannot be empty';
          if (value.length > 50000) return 'Message too long (max 50k chars)';
          return null;
        },
        examples: [
          'Can you expand on that?',
          'Make it more detailed'
        ]
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { message: 'Tell me more about this topic' },
        expectedOutput: createSuccessResult('Message sent successfully', {
          messageSent: true,
          messageLength: 26
        }),
        description: 'Send a follow-up message',
        context: 'When continuing a conversation'
      }
    ],
    bad: [
      {
        input: { message: '' },
        error: 'Message cannot be empty',
        why: 'Empty messages are not allowed',
        fix: 'Provide a non-empty message'
      }
    ]
  },
  
  performance: {
    expectedDuration: 50,
    complexity: 'low'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { message } = params;
    
    try {
      log.log('[chat_sendMessage] Sending message to current thread:', {
        messageLength: message?.length,
        hasContext: !!context
      });
      
      // For now, this command is a placeholder
      // The actual implementation would need to integrate with SpaceChat's message sending
      // This could dispatch an event or call a method on the chat store
      
      log.log('[chat_sendMessage] ⚠️ Not yet implemented - use chat_createThread for new conversations');
      
      return createSuccessResult(
        `✅ Message queued (${message.length} chars)`,
        {
          messageSent: false,
          messageLength: message.length,
          note: 'Use chat_createThread to start a new conversation with a message'
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'placeholder',
            affectedEntities: []
          },
          ui: {
            refreshContext: false,
            resultPreview: 'summary'
          }
        }
      );
      
    } catch (error) {
      log.error('[chat_sendMessage] Error:', error);
      return createErrorResult(
        'Failed to send message',
        error instanceof Error ? error.message : 'Unknown error',
        'execution',
        { 
          data: { 
            messageLength: message?.length || 0
          },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};

